<template>
  <div
    class="modal fade"
    ref="modalEl"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">
            <i :class="editMode ? 'fas fa-user-edit me-2' : 'fas fa-user-plus me-2'"></i>
            {{ editMode ? 'Editar Cliente' : 'Registrar Cliente' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="close"></button>
        </div>
        <div class="modal-body" style="max-height: 75vh; overflow-y: auto;">
          <form novalidate @submit.prevent="submit">
            <div class="container-fluid">
              <div v-if="formError" class="alert alert-danger" role="alert">
                {{ formError }}
              </div>
              <!-- Sección: Información Básica -->
              <div class="section-header mb-3">
                <h6 class="text-primary border-bottom pb-2 mb-3">
                  <i class="fas fa-id-card me-2"></i>Información Básica
                </h6>
              </div>
              
              <!-- CR-RF-013 · RF-3, RF-4. UN campo de documento y un select de
                   tipo, en vez de `Nro. Documento` + `RUC` los dos obligatorios.
                   `ruc`, `ci` y `nro_documento` se derivan al guardar.

                   Obligatorio pasa a ser «un documento», no «un RUC»: eso cierra
                   el GAP de CR-E6-003, que impedía dar de alta a un responsable
                   sin identificación fiscal y bloqueaba RN-7 / RN-44. -->
              <div class="row mb-3">
                <div class="col-md-5">
                  <label class="form-label fw-bold">
                    Documento <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input
                      type="text"
                      v-model="form.documento"
                      class="form-control"
                      :class="{ 'is-invalid': validationErrors.documento }"
                      placeholder="Con o sin dígito verificador"
                      data-test="cliente-documento"
                      @blur="buscarEnPadron"
                      @keyup.enter="buscarEnPadron"
                    />
                    <span v-if="padronCargando" class="input-group-text" data-test="padron-cargando">
                      <span class="spinner-border spinner-border-sm"></span>
                    </span>
                  </div>
                  <!-- El padrón informa y no frena (RF-16), con UNA excepción:
                       un RUC con DV que no figura no se guarda como RUC
                       (iniciativa AC, RN-189). Ese caso va en rojo y con salida. -->
                  <small v-if="padronMensaje" class="d-block mt-1" data-test="padron-mensaje"
                         :class="padronRucInvalido ? 'text-danger' : 'text-muted'">
                    {{ padronMensaje }}
                  </small>
                  <button v-if="padronRucInvalido && padronCedula" type="button"
                          class="btn btn-outline-secondary btn-sm mt-1" data-test="padron-usar-cedula"
                          @click="usarComoCedula">
                    Registrar con la cédula {{ padronCedula }}
                  </button>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Tipo de Documento</label>
                  <!-- Modificable: se prellena con lo derivado del DV y lo que
                       el operador elija gana (RF-3). -->
                  <select v-model="form.tipo_documento" class="form-select" data-test="cliente-tipo-documento">
                    <option value="RUC">RUC</option>
                    <option value="CI">Cédula de Identidad</option>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="CEDULA_EXTRANJERA">Cédula extranjera</option>
                    <option value="CARNET_RESIDENCIA">Carnet de residencia</option>
                    <option value="NO_ESPECIFICADO">Sin especificar</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Nombre Completo <span class="text-danger">*</span></label>
                  <input type="text" v-model="form.nombre_completo" class="form-control" :class="{ 'is-invalid': validationErrors.nombre_completo }" data-test="cliente-nombre" required />
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold">Nombre Fantasía</label>
                  <input type="text" v-model="form.nombre_fantasia" class="form-control" />
                </div>
              </div>

              <!-- Sección: Identificación Detallada -->
              <div class="section-header mb-3 mt-4">
                <h6 class="text-primary border-bottom pb-2 mb-3">
                  <i class="fas fa-address-card me-2"></i>Identificación Detallada
                </h6>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold">Tipo de Persona</label>
                  <!-- CR-RF-013 · RF-6. **Se sugiere, no se deriva.** No existe
                       fuente autoritativa del tipo de persona: ni la consulta
                       RUC en vivo de SIFEN ni el padrón DNIT lo traen. Pero el
                       dato tiene efecto fiscal directo — es `iTiContRec` del
                       Manual Técnico SIFEN v150 — así que lo confirma quien
                       conoce al cliente.

                       El label dice «Tipo de Persona» y no «Naturaleza»: el
                       contrato de FE llama `naturaleza` a `iTiContRec`, pero
                       en el manual `iNatRec` es OTRO campo. -->
                  <select v-model="form.identificacion.tipo_persona" class="form-select"
                          data-test="cliente-tipo-persona">
                    <option value="fisica">Persona Física</option>
                    <option value="juridica">Persona Jurídica</option>
                  </select>
                  <small v-if="tipoPersonaSugerido" class="text-muted d-block mt-1"
                         data-test="tipo-persona-sugerido">
                    Sugerido por el padrón. Confirmalo o cambialo.
                  </small>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Tipo de Documento</label>
                  <select v-model="form.identificacion.tipo_documento_id" class="form-select">
                    <option value="1">Cédula de Identidad</option>
                    <option value="2">RUC</option>
                    <option value="3">Pasaporte</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Género</label>
                  <select v-model="form.identificacion.genero_id" class="form-select">
                    <option value="1">Masculino</option>
                    <option value="2">Femenino</option>
                    <option value="3">Otro</option>
                  </select>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold">Nombres</label>
                  <input type="text" v-model="form.identificacion.nombres" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Apellidos</label>
                  <input type="text" v-model="form.identificacion.apellidos" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold">Fecha de Nacimiento</label>
                  <input
                    type="text"
                    :value="form.identificacion.fecha_nacimiento"
                    class="form-control"
                    placeholder="dd/mm/aaaa"
                    inputmode="numeric"
                    maxlength="10"
                    @input="handleFechaNacimientoInput"
                  />
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold">Razón Social</label>
                  <!-- RN-194. Con RUC confirmado, es la del padrón y no se edita:
                       cambiar el documento la libera. -->
                  <input type="text" v-model="form.identificacion.razon_social" class="form-control"
                         :readonly="!!razonSocialDelPadron"
                         :class="{ 'is-invalid': validationErrors.razon_social }"
                         data-test="cliente-razon-social" />
                  <small v-if="razonSocialDelPadron" class="text-muted d-block mt-1" data-test="razon-social-padron">
                    Según el padrón de la DNIT. Es el nombre con el que se factura.
                  </small>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Estado de Relación</label>
                  <select v-model="form.identificacion.estado_relacion_id" class="form-select">
                    <option value="1">Soltero/a</option>
                    <option value="2">Casado/a</option>
                    <option value="3">Divorciado/a</option>
                    <option value="4">Viudo/a</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Etapa Cliente</label>
                  <select v-model="form.identificacion.etapa_cliente_id" class="form-select">
                    <option value="1">Prospecto</option>
                    <option value="2">Cliente Nuevo</option>
                    <option value="3">Cliente Regular</option>
                    <option value="4">Cliente VIP</option>
                  </select>
                </div>
              </div>

              <!-- Sección: Información de Contacto -->
              <div class="section-header mb-3 mt-4">
                <h6 class="text-primary border-bottom pb-2 mb-3">
                  <i class="fas fa-phone me-2"></i>Información de Contacto
                </h6>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold">Teléfono Principal</label>
                  <input type="tel" v-model="form.telefono" class="form-control"
                         :class="{ 'is-invalid': validationErrors.telefono }"
                         data-test="cliente-telefono" />
                  <small class="text-muted">Teléfono principal para contacto</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">Email Principal</label>
                  <input type="email" v-model="form.email" class="form-control"
                         :class="{ 'is-invalid': validationErrors.email }"
                         maxlength="50" data-test="cliente-email" />
                  <small class="text-muted">Email principal para comunicaciones</small>
                </div>
              </div>

              <!-- Teléfonos Adicionales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Teléfonos Adicionales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(telefono, index) in form.contacto.telefonos" :key="index" class="row mb-2">
                      <div class="col-md-4">
                        <select v-model="telefono.tipo_contacto_id" class="form-select form-select-sm">
                          <option value="1">Teléfono Fijo</option>
                          <option value="2">Celular</option>
                          <option value="3">WhatsApp</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <input type="tel" v-model="telefono.valor" class="form-control form-control-sm" placeholder="Número de teléfono" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeTelefono(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addTelefono">
                      <i class="fas fa-plus me-1"></i>Agregar Teléfono
                    </button>
                  </div>
                </div>
              </div>

              <!-- Emails Adicionales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Emails Adicionales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(email, index) in form.contacto.emails" :key="index" class="row mb-2">
                      <div class="col-md-4">
                        <select v-model="email.tipo_contacto_id" class="form-select form-select-sm">
                          <option value="3">Email Personal</option>
                          <option value="4">Email Trabajo</option>
                          <option value="5">Email Facturación</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <input type="email" v-model="email.valor" class="form-control form-control-sm" placeholder="Dirección de email" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeEmail(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addEmail">
                      <i class="fas fa-plus me-1"></i>Agregar Email
                    </button>
                  </div>
                </div>
              </div>

              <!-- Redes Sociales -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Redes Sociales</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(red, index) in form.contacto.redes" :key="index" class="row mb-2">
                      <div class="col-md-3">
                        <select v-model="red.red_social_id" class="form-select form-select-sm">
                          <option value="1">Twitter</option>
                          <option value="2">Facebook</option>
                          <option value="3">Instagram</option>
                          <option value="4">LinkedIn</option>
                          <option value="5">TikTok</option>
                        </select>
                      </div>
                      <div class="col-md-3">
                        <input type="text" v-model="red.handle" class="form-control form-control-sm" placeholder="@usuario" />
                      </div>
                      <div class="col-md-4">
                        <input type="url" v-model="red.url" class="form-control form-control-sm" placeholder="URL completa" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeRed(index)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRed">
                      <i class="fas fa-plus me-1"></i>Agregar Red Social
                    </button>
                  </div>
                </div>
              </div>

              <!-- Sección: Direcciones -->
              <div class="section-header mb-3 mt-4">
                <h6 class="text-primary border-bottom pb-2 mb-3">
                  <i class="fas fa-map-marker-alt me-2"></i>Direcciones
                </h6>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold">Dirección Principal</label>
                  <input type="text" v-model="form.direccion" class="form-control" />
                  <small class="text-muted">Dirección principal del cliente</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold">Condiciones de Pago</label>
                  <select v-model="form.condiciones_pago" class="form-select">
                    <option value="CONTADO">Contado</option>
                    <option value="CREDITO">Crédito</option>
                  </select>
                </div>
              </div>

              <!-- Direcciones Detalladas -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label fw-bold">Direcciones Detalladas</label>
                  <div class="border rounded p-3 bg-light">
                    <div v-for="(direccion, index) in form.direcciones" :key="index" class="border rounded p-3 mb-3 bg-white">
                      <div class="row mb-2">
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Tipo de Dirección</label>
                          <select v-model="direccion.tipo_direccion_id" class="form-select form-select-sm">
                            <option value="1">Domicilio</option>
                            <option value="2">Trabajo</option>
                            <option value="3">Comercial</option>
                            <option value="4">Facturación</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">País</label>
                          <input type="text" v-model="direccion.pais" class="form-control form-control-sm" placeholder="Paraguay" />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Departamento</label>
                          <select v-model="direccion.departamento_id" class="form-select form-select-sm">
                            <option value="1">Concepción</option>
                            <option value="2">San Pedro</option>
                            <option value="3">Cordillera</option>
                            <option value="4">Guairá</option>
                            <option value="5">Central</option>
                            <option value="6">Caaguazú</option>
                            <option value="7">Caazapá</option>
                          </select>
                        </div>
                      </div>
                      <div class="row mb-2">
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Ciudad</label>
                          <select v-model="direccion.ciudad_id" class="form-select form-select-sm">
                            <option value="101">Asunción</option>
                            <option value="102">San Lorenzo</option>
                            <option value="103">Lambaré</option>
                            <option value="104">Fernando de la Mora</option>
                            <option value="105">Luque</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Barrio</label>
                          <input type="text" v-model="direccion.barrio" class="form-control form-control-sm" placeholder="Nombre del barrio" />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label fw-bold">Calle Principal</label>
                          <input type="text" v-model="direccion.calle_principal" class="form-control form-control-sm" placeholder="Nombre de la calle" />
                        </div>
                      </div>
                      <div class="row mb-2">
                        <div class="col-md-3">
                          <label class="form-label fw-bold">Número</label>
                          <input type="text" v-model="direccion.numero" class="form-control form-control-sm" placeholder="Nro." />
                        </div>
                        <div class="col-md-5">
                          <label class="form-label fw-bold">Referencia</label>
                          <input type="text" v-model="direccion.referencia" class="form-control form-control-sm" placeholder="Referencias adicionales" />
                        </div>
                        <div class="col-md-2">
                          <label class="form-label fw-bold">Latitud</label>
                          <input type="number" step="0.0001" v-model="direccion.lat" class="form-control form-control-sm" placeholder="-25.3001" />
                        </div>
                        <div class="col-md-2">
                          <label class="form-label fw-bold">Longitud</label>
                          <input type="number" step="0.0001" v-model="direccion.lng" class="form-control form-control-sm" placeholder="-57.6359" />
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-10">
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="direccion.es_principal" :id="'principal-' + index">
                            <label class="form-check-label" :for="'principal-' + index">
                              Dirección principal
                            </label>
                          </div>
                        </div>
                        <div class="col-md-2 text-end">
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="removeDireccion(index)">
                            <i class="fas fa-trash"></i> Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addDireccion">
                      <i class="fas fa-plus me-1"></i>Agregar Dirección
                    </button>
                  </div>
                </div>
              </div>

              <!-- Botones de Acción -->
              <div class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-end gap-2">
                    <button type="button" class="btn btn-outline-secondary" @click="close">
                      <i class="fas fa-times me-1"></i>Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                      <i :class="editMode ? 'fas fa-edit me-1' : 'fas fa-save me-1'"></i>
                      {{ editMode ? 'Actualizar Cliente' : 'Guardar Cliente' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import apiService from '@/services/apiService.js';
import { partirRuc, avisoDePadron, nombrePareceDocumento } from '@/utils/padronRuc.mjs';

export default {
  name: 'RegistrarClienteModal',
  props: {
    open: { type: Boolean, default: false },
    prefillRuc: { type: String, default: '' },
    prefillDocumento: { type: String, default: '' },
    editMode: { type: Boolean, default: false },
    clienteData: { type: Object, default: () => null }
  },
  emits: ['saved', 'close'],
  data() {
    return {
      bsModal: null,
      formError: '',
      validationErrors: {},
      // CR-RF-013. Estado del autocompletado contra el padrón DNIT.
      // `padronMensaje` informa y NUNCA bloquea (RF-16): «no está en el
      // padrón» y «el padrón no contesta» terminan los dos en carga manual.
      padronCargando: false,
      padronMensaje: '',
      tipoPersonaSugerido: false,
      ultimoDocumentoConsultado: '',
      // Iniciativa AC, RN-189. Un RUC con DV que el padrón no tiene: no se
      // guarda como RUC. Es la única respuesta del padrón que bloquea.
      padronRucInvalido: false,
      padronCedula: '',
      // RN-191. En edición se consulta sólo si el documento cambió.
      documentoOriginal: '',
      // RN-194. La razón social que confirmó el padrón; mientras tenga valor,
      // el campo no se edita.
      razonSocialDelPadron: '',
      form: {
        // CR-RF-013 · RF-3, RF-4. UN documento y su tipo. `ruc`, `ci` y
        // `nro_documento` se derivan de este par al guardar.
        documento: '',
        tipo_documento: 'CI',
        // Campos originales (obligatorios)
        nombre_completo: '',
        nro_documento: '',
        ruc: '',
        direccion: '',
        telefono: '',
        email: '',
        nombre_fantasia: '',
        condiciones_pago: 'CONTADO',
        
        // Nuevos campos - Identificación
        identificacion: {
          tipo_persona: 'fisica',
          tipo_documento_id: 1,
          nombres: '',
          apellidos: '',
          razon_social: '',
          genero_id: 1,
          fecha_nacimiento: '',
          estado_relacion_id: 1,
          etapa_cliente_id: 1
        },
        
        // Nuevos campos - Contacto
        contacto: {
          telefonos: [],
          emails: [],
          redes: []
        },
        
        // Nuevos campos - Direcciones
        direcciones: []
      }
    };
  },
  watch: {
    open(val) {
      if (val) this.show(); else this.hide();
    },
    // RN-189. El aviso rojo es sobre el documento consultado: si el operador
    // lo cambia, deja de bloquear hasta la próxima consulta.
    'form.documento'(valor) {
      if (String(valor || '').trim() === this.ultimoDocumentoConsultado) return;
      if (this.padronRucInvalido) {
        this.padronRucInvalido = false;
        this.padronCedula = '';
        this.padronMensaje = '';
      }
      // RN-194. Otro documento: la razón social deja de estar confirmada.
      this.razonSocialDelPadron = '';
    }
  },
  methods: {
    normalizeDateDigits(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 8);
    },
    applyDateMask(value) {
      const digits = this.normalizeDateDigits(value);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    },
    formatDateForDisplay(value) {
      if (!value) return '';
      const raw = String(value).trim();
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [year, month, day] = raw.split('-');
        return `${day}/${month}/${year}`;
      }
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return this.applyDateMask(raw);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    convertDisplayDateToISO(value) {
      const formatted = this.formatDateForDisplay(value);
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) return '';
      const [day, month, year] = formatted.split('/');
      return `${year}-${month}-${day}`;
    },
    handleFechaNacimientoInput(event) {
      this.form.identificacion.fecha_nacimiento = this.applyDateMask(event.target.value);
    },
    show() {
      this.formError = '';
      this.validationErrors = {};
      if (!this.bsModal) {
        this.bsModal = new Modal(this.$refs.modalEl, {
          backdrop: 'static',
          keyboard: false
        });
      }
      
      this.padronMensaje = '';
      this.padronRucInvalido = false;
      this.padronCedula = '';
      this.razonSocialDelPadron = '';
      this.ultimoDocumentoConsultado = '';
      this.documentoOriginal = this.editMode && this.clienteData
        ? String(this.clienteData.ruc || this.clienteData.ci || this.clienteData.nro_documento || '').trim()
        : '';

      if (this.editMode && this.clienteData) {
        // Modo edición: precargar datos del cliente
        this.form = {
          // CR-RF-013. El campo visible se arma con la misma precedencia que
          // usa la emisión (`ruc -> ci -> nro_documento`, RF-9): así el
          // operador ve el documento con el que se va a facturar, y no uno
          // distinto según qué columna se hubiera llenado primero.
          documento: this.clienteData.ruc || this.clienteData.ci
            || this.clienteData.nro_documento || '',
          tipo_documento: this.detectarTipoDocumento(
            this.clienteData.ruc || this.clienteData.ci || this.clienteData.nro_documento || ''),
          // Campos originales (obligatorios) - precargados
          nombre_completo: this.clienteData.nombre_completo || '',
          nro_documento: this.clienteData.nro_documento || '',
          ruc: this.clienteData.ruc || '',
          direccion: this.clienteData.direccion || '',
          telefono: this.clienteData.telefono || '',
          email: this.clienteData.email || '',
          nombre_fantasia: this.clienteData.nombre_fantasia || '',
          condiciones_pago: this.clienteData.condiciones_pago || 'CONTADO',
          
          // Nuevos campos - Identificación (con valores por defecto si no existen)
          identificacion: {
            tipo_persona: this.clienteData.identificacion?.tipo_persona || 'fisica',
            tipo_documento_id: this.clienteData.identificacion?.tipo_documento_id || 1,
            nombres: this.clienteData.identificacion?.nombres || '',
            apellidos: this.clienteData.identificacion?.apellidos || '',
            razon_social: this.clienteData.identificacion?.razon_social || '',
            genero_id: this.clienteData.identificacion?.genero_id || 1,
            fecha_nacimiento: this.formatDateForDisplay(this.clienteData.identificacion?.fecha_nacimiento || ''),
            estado_relacion_id: this.clienteData.identificacion?.estado_relacion_id || 1,
            etapa_cliente_id: this.clienteData.identificacion?.etapa_cliente_id || 1
          },
          
          // Nuevos campos - Contacto (con arrays por defecto si no existen)
          contacto: {
            telefonos: this.clienteData.contacto?.telefonos || [],
            emails: this.clienteData.contacto?.emails || [],
            redes: this.clienteData.contacto?.redes || []
          },
          
          // Nuevos campos - Direcciones (con array por defecto si no existe)
          direcciones: this.clienteData.direcciones || []
        };
      } else {
        // Modo creación: formulario vacío con prefill básico
        this.form = {
          documento: this.prefillRuc || this.prefillDocumento || '',
          tipo_documento: this.detectarTipoDocumento(this.prefillRuc || this.prefillDocumento || ''),
          // Campos originales (obligatorios)
          nombre_completo: '',
          nro_documento: this.prefillDocumento || this.prefillRuc || '',
          ruc: this.prefillRuc || this.prefillDocumento || '',
          direccion: '',
          telefono: '',
          email: '',
          nombre_fantasia: '',
          condiciones_pago: 'CONTADO',
          
          // Nuevos campos - Identificación
          identificacion: {
            tipo_persona: 'fisica',
            tipo_documento_id: 1,
            nombres: '',
            apellidos: '',
            razon_social: '',
            genero_id: 1,
            fecha_nacimiento: '',
            estado_relacion_id: 1,
            etapa_cliente_id: 1
          },
          
          // Nuevos campos - Contacto
          contacto: {
            telefonos: [],
            emails: [],
            redes: []
          },
          
          // Nuevos campos - Direcciones
          direcciones: []
        };
      }
      
      this.bsModal.show();
    },
    hide() {
      this.bsModal?.hide();
    },
    close() {
      this.formError = '';
      this.validationErrors = {};
      this.hide();
      this.$emit('close');
    },
    resolveErrorMessage(error, fallback) {
      return error?.userMessage
        || error?.normalized?.message
        || error?.response?.data?.message
        || error?.response?.data?.error
        || fallback;
    },
    notifyError(message) {
      this.formError = message;
      if (window.alert?.error) {
        window.alert.error(message);
      } else {
        window.alert(message);
      }
    },
    // CR-RF-013 · RF-3. **El tipo lo decide la FORMA del valor, no la columna.**
    // Mismo criterio que `domain/fiscal/receptor.js` en el backend: con DV hay
    // RUC, sin DV es cédula. Está duplicado a propósito y es la única
    // duplicación aceptada — el backend NO confía en lo que mande el formulario
    // y vuelve a derivar al emitir; esto sólo prellena un campo editable.
    detectarTipoDocumento(valor) {
      const v = String(valor || '').trim();
      if (!v || !/\d/.test(v)) return 'CI';
      if (v.includes('-')) return 'RUC';
      if (v.length > 8) return 'RUC';
      return 'CI';
    },

    // RF-1, RF-2. Primero adentro, después afuera.
    //
    // **Nada se guarda solo.** Lo que vuelve prellena campos editables; si el
    // operador no toca nada se guarda lo propuesto, pero porque apretó Guardar.
    // El padrón es una foto mensual y el operador tiene delante a la persona.
    async buscarEnPadron() {
      const doc = String(this.form.documento || '').trim();

      // El tipo se propone en cuanto hay un documento, con o sin padrón.
      if (doc) this.form.tipo_documento = this.detectarTipoDocumento(doc);

      // Sólo tiene sentido para RUC y CI: un pasaporte no está en este padrón
      // (RF-3). Y no se repite la consulta del mismo documento: `blur` dispara
      // cada vez que el campo pierde el foco.
      if (!doc || doc === this.ultimoDocumentoConsultado) return;
      if (!['RUC', 'CI'].includes(this.form.tipo_documento)) return;
      // RN-191. La edición consulta igual que el alta, pero sólo si el
      // documento cambió: editar el teléfono no tiene que esperar al padrón.
      if (this.editMode && doc === this.documentoOriginal) return;

      this.ultimoDocumentoConsultado = doc;
      this.padronCargando = true;
      this.padronMensaje = '';
      this.padronRucInvalido = false;
      this.padronCedula = '';

      try {
        // Por la BASE: con DV, el loader filtra por los dos y un dígito mal
        // tipeado volvía «no figura» en vez de decir cuál es el correcto.
        const { base } = partirRuc(doc);
        const { data } = await apiService.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/clients/padron/${encodeURIComponent(base)}`);
        const p = data?.data;
        if (!p) return;

        const aviso = avisoDePadron(doc, p.ruc);

        // Prellenado: sólo lo que está vacío, salvo el documento y su tipo, que
        // el padrón sí corrige — es lo que aporta el DV.
        this.form.documento = aviso.documento || p.sugerencia?.documento || this.form.documento;
        this.ultimoDocumentoConsultado = this.form.documento;
        this.form.tipo_documento = p.sugerencia?.tipo_documento || this.form.tipo_documento;
        if (!this.form.nombre_completo) {
          this.form.nombre_completo = p.sugerencia?.nombre_completo || '';
        }
        if (!this.form.identificacion.nombres) this.form.identificacion.nombres = p.nombre || '';
        if (!this.form.identificacion.apellidos) this.form.identificacion.apellidos = p.apellido || '';
        // RN-194. La razón social de un RUC es la del padrón, aunque el
        // operador haya escrito otra: es con la que se factura. Antes sólo se
        // prellenaba si estaba vacía, y así salió «Nestor Avalos» en vez de
        // «NESTOR CARMELO AVALOS MERELES». El backend la vuelve a poner igual.
        if (p.razon_social) {
          this.form.identificacion.razon_social = p.razon_social;
          this.razonSocialDelPadron = p.razon_social;
        }
        if (p.sugerencia?.tipo_persona) {
          this.form.identificacion.tipo_persona = p.sugerencia.tipo_persona;
          this.tipoPersonaSugerido = true;
        }
        const nombreFiscal = p.razon_social ? ` Se factura a nombre de ${p.razon_social}.` : '';
        this.padronMensaje = aviso.estado === 'CORREGIDO'
          ? `${aviso.mensaje}${nombreFiscal}`
          : `Encontrado en el padrón${p.estado ? ` (${p.estado})` : ''}.${nombreFiscal} Revisá y completá lo que falte.`;
      } catch (e) {
        // 503 —el padrón no contestó— no bloquea: carga manual, como siempre
        // (RN-193). 404 tampoco, SALVO que lo tipeado sea un RUC con DV: ese
        // RUC no existe y SIFEN rechazaría la factura (RN-189, H-AS).
        const sit = e?.normalized?.situation || e?.response?.data?.situation;
        if (sit === 'padron_documento_no_encontrado') {
          const aviso = avisoDePadron(doc, null);
          this.padronMensaje = aviso.mensaje;
          this.padronRucInvalido = aviso.estado === 'NO_FIGURA';
          this.padronCedula = aviso.cedula || '';
        } else {
          this.padronMensaje = 'No se pudo consultar el padrón. Cargá los datos a mano.';
        }
      } finally {
        this.padronCargando = false;
      }
    },

    // RN-189. La salida que se ofrece cuando el RUC no figura: guardarlo con
    // la cédula, que factura bien (hay facturas a CI aprobadas por SIFEN).
    usarComoCedula() {
      if (!this.padronCedula) return;
      this.form.documento = this.padronCedula;
      this.form.tipo_documento = 'CI';
      this.ultimoDocumentoConsultado = this.padronCedula;
      this.padronRucInvalido = false;
      this.padronCedula = '';
      this.padronMensaje = 'Se registrará con la cédula.';
    },

    // RF-3. Del par (documento, tipo) salen las tres columnas.
    //
    // Para un RUC se guardan las DOS: `ruc` con DV y `ci` con la base. Un RUC
    // paraguayo de persona física ES la cédula más el dígito, y quien busque
    // por cédula —el buscador del POS acepta las tres columnas— tiene que
    // encontrarlo igual.
    columnasDeDocumento() {
      const doc = String(this.form.documento || '').trim();
      const tipo = this.form.tipo_documento;
      if (!doc) return { ruc: '', ci: '', nro_documento: '' };
      if (tipo === 'RUC') {
        const base = doc.includes('-') ? doc.slice(0, doc.indexOf('-')).trim() : doc;
        return { ruc: doc, ci: base, nro_documento: doc };
      }
      if (tipo === 'CI') return { ruc: '', ci: doc, nro_documento: doc };
      return { ruc: '', ci: '', nro_documento: doc };
    },

    validateClientForm() {
      const errors = {};
      // CR-RF-013 · RF-4. **Obligatorio es «un documento», no «un RUC».**
      //
      // Antes se exigían tres: nombre, `nro_documento` y RUC. Quien no tenía
      // RUC tipeaba la cédula en ese campo para poder guardar — de ahí salen
      // las cédulas que hoy viven en `clientes.ruc` y que RF-3 tiene que
      // desambiguar por la forma del valor.
      //
      // Y bloqueaba RN-7 / RN-44: un responsable sin identificación fiscal NO
      // se podía crear, así que el camino «cobrale con recibo» existía en el
      // API y no tenía forma de ejercerse. Es el GAP de `CR-E6-003`, y se
      // cierra acá.
      const requiredFields = {
        nombre_completo: 'Nombre completo',
        documento: 'Documento'
      };

      Object.entries(requiredFields).forEach(([field]) => {
        if (!String(this.form[field] || '').trim()) {
          errors[field] = true;
        }
      });

      // RF-7. Forma **y longitud**. `clientes.email` admite 50: sin este
      // chequeo un correo de 60 caracteres pasaba la validación y moría en el
      // INSERT con `22001`, que al operador le llega como «Error al registrar
      // el cliente» — sin nombrar el campo ni el límite (hallazgo H-RF3).
      const email = String(this.form.email || '').trim();
      if (email && (!/\S+@\S+\.\S+/.test(email) || email.length > 50)) {
        errors.email = true;
      }

      // RF-8. El teléfono se escribe con separadores, pero termina en dígitos:
      // facturación electrónica rechaza con 422 lo que pase de 15.
      const telefono = String(this.form.telefono || '').trim();
      if (telefono) {
        if (!/^[\d\s()+-]+$/.test(telefono)) errors.telefono = true;
        else if (telefono.replace(/\D/g, '').length > 15) errors.telefono = true;
      }

      this.validationErrors = errors;

      if (!Object.keys(errors).length) {
        this.formError = '';
        return true;
      }

      // RN-195. Un número de documento no es un nombre: así salió una factura
      // con «3829909-0» como razón social.
      const nombreDocumento = [this.form.identificacion.razon_social, this.form.nombre_completo]
        .find((n) => nombrePareceDocumento(n));
      if (nombreDocumento) {
        if (nombrePareceDocumento(this.form.identificacion.razon_social)) errors.razon_social = true;
        if (nombrePareceDocumento(this.form.nombre_completo)) errors.nombre_completo = true;
        this.validationErrors = errors;
        this.notifyError(`«${String(nombreDocumento).trim()}» es un número de documento, no un nombre. `
          + 'Cargá el nombre o la razón social de la persona.');
        return false;
      }

      // RN-189. El backend lo rechaza igual (RN-190); esto lo dice antes.
      if (this.padronRucInvalido) {
        errors.documento = true;
        this.validationErrors = errors;
        this.notifyError(this.padronMensaje);
        return false;
      }

      if (errors.email && this.form.email) {
        this.notifyError(String(this.form.email).trim().length > 50
          ? 'El correo no puede superar los 50 caracteres.'
          : 'El email ingresado no es válido.');
      } else if (errors.telefono && this.form.telefono) {
        this.notifyError(/^[\d\s()+-]+$/.test(String(this.form.telefono).trim())
          ? 'El teléfono no puede tener más de 15 dígitos: es el límite de facturación electrónica.'
          : 'El teléfono sólo puede tener números, espacios, guiones y paréntesis.');
      } else {
        const missingLabels = Object.entries(requiredFields)
          .filter(([field]) => errors[field])
          .map(([, label]) => label);
        this.notifyError(`Complete los campos obligatorios: ${missingLabels.join(', ')}.`);
      }
      return false;
    },
    async submit() {
      if (!this.validateClientForm()) return;

      try {
        let response;
        
        if (this.editMode && this.clienteData) {
          // Modo edición: actualizar cliente existente
          const clienteId = this.clienteData.cliente_id || this.clienteData.id;
          
          // CR-RF-013. Las tres columnas salen del par (documento, tipo).
          //
          // Antes hacía `ci: this.form.nro_documento` sin mirar el tipo: a un
          // cliente con RUC le escribía el RUC completo —con guion y dígito—
          // en la columna `ci`. Eso rompe el buscador por cédula y, peor, deja
          // dos verdades distintas sobre el mismo documento.
          const cols = this.columnasDeDocumento();
          const body = {
            nombre_completo: this.form.nombre_completo,
            ci: cols.ci,
            nro_documento: cols.nro_documento,
            ruc: cols.ruc,
            direccion: this.form.direccion,
            telefono: this.form.telefono,
            email: this.form.email,
            nombre_fantasia: this.form.nombre_fantasia,
            condiciones_pago: this.form.condiciones_pago,
            // La edición también persiste la identidad: si sólo la escribiera
            // el alta, corregir el tipo de persona de un cliente ya cargado no
            // tendría efecto y `naturaleza` seguiría saliendo mal (RF-12).
            identificacion: {
              tipo_persona: this.form.identificacion.tipo_persona,
              nombres: this.form.identificacion.nombres,
              apellidos: this.form.identificacion.apellidos,
              razon_social: this.form.identificacion.razon_social
            }
          };
          
          response = await apiService.put(
            `${process.env.VUE_APP_API_BASE_URL}/api/clients/${clienteId}`, 
            body
          );
        } else {
          // Modo creación. `documento` y `tipo_documento` son del formulario,
          // no del modelo de datos: se traducen a las tres columnas y no se
          // mandan como tales.
          const cols = this.columnasDeDocumento();
          const body = {
            ...this.form,
            ...cols,
            identificacion: {
              ...this.form.identificacion,
              fecha_nacimiento: this.convertDisplayDateToISO(this.form.identificacion.fecha_nacimiento)
            }
          };
          delete body.documento;
          delete body.tipo_documento;
          response = await apiService.post(`${process.env.VUE_APP_API_BASE_URL}/api/clients`, body);
        }
        
        this.$emit('saved', response.data?.cliente || response.data);
        this.close();
      } catch (e) {
        console.error(`Error al ${this.editMode ? 'actualizar' : 'guardar'} el cliente:`, e);
        this.notifyError(this.resolveErrorMessage(e, `No se pudo ${this.editMode ? 'actualizar' : 'guardar'} el cliente.`));
      }
    },

    // Métodos para manejar teléfonos
    addTelefono() {
      this.form.contacto.telefonos.push({
        contacto_id: null,
        tipo_contacto_id: 1,
        valor: '',
        es_principal: false
      });
    },
    removeTelefono(index) {
      this.form.contacto.telefonos.splice(index, 1);
    },

    // Métodos para manejar emails
    addEmail() {
      this.form.contacto.emails.push({
        contacto_id: null,
        tipo_contacto_id: 3,
        valor: '',
        es_principal: false
      });
    },
    removeEmail(index) {
      this.form.contacto.emails.splice(index, 1);
    },

    // Métodos para manejar redes sociales
    addRed() {
      this.form.contacto.redes.push({
        cliente_red_id: null,
        red_social_id: 1,
        handle: '',
        url: '',
        es_principal: false
      });
    },
    removeRed(index) {
      this.form.contacto.redes.splice(index, 1);
    },

    // Métodos para manejar direcciones
    addDireccion() {
      this.form.direcciones.push({
        direccion_id: null,
        tipo_direccion_id: 1,
        pais: 'Paraguay',
        departamento_id: 5,
        ciudad_id: 101,
        barrio: '',
        calle_principal: '',
        numero: '',
        referencia: '',
        lat: null,
        lng: null,
        es_principal: false
      });
    },
    removeDireccion(index) {
      this.form.direcciones.splice(index, 1);
    }
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.modalEl);
    if (this.open) this.show();
  },
  beforeUnmount() {
    this.bsModal?.hide();
    this.bsModal = null;
  }
};
</script>

<style scoped>
.modal-xl {
  max-width: 1200px;
}

.section-header {
  margin-top: 1.5rem;
}

.section-header:first-of-type {
  margin-top: 0;
}

.section-header h6 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-label.fw-bold {
  font-size: 0.9rem;
  color: #333;
}

.text-danger {
  color: #dc3545 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.border {
  border: 1px solid #dee2e6 !important;
}

.rounded {
  border-radius: 0.375rem !important;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.form-select-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.gap-2 {
  gap: 0.5rem !important;
}

.modal-body {
  padding: 1.5rem;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* Mejoras en el scroll */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Efectos hover para botones */
.btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease-in-out;
}

/* Mejora visual para las secciones */
.bg-white {
  background-color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }
  
  .modal-body {
    padding: 1rem;
  }
}
</style>
